import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons/16px';
import { capitalize } from 'lodash';
import { ReactNode } from 'react';
import styled from 'styled-components';

import DeviceIcon from 'dashboard/components/DeviceIcon';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { DevicesModuleAttribute } from 'dashboard/lib/case-layouts/modules/devices';
import { Device, DeviceType } from 'dashboard/models/device';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { ProductFeatureName } from 'dashboard/models/organization';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import IconGlobe from 'images/icons/globe.svg';
import useProductFeature from 'lib/hooks/useProductFeature';

const IconAlertCircleStyled = styled(CircleAlertIcon)`
  margin-left: ${Spacing[2]};
  margin-bottom: -2px;
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Column = styled.div`
  display: block;
  width: 100%;
`;

interface InquiryDeviceSessionProps {
  layoutNetwork?: DevicesModuleAttribute[];
  layoutDevice?: DevicesModuleAttribute[];
  inquirySession: InquirySession;
  device?: Device;
  isRedacted: boolean;
}
const DEFAULT_LAYOUT_NETWORK = [
  DevicesModuleAttribute.IpAddress,
  DevicesModuleAttribute.ThreatRating,
  DevicesModuleAttribute.NetworkCountry,
  DevicesModuleAttribute.NetworkRegion,
  DevicesModuleAttribute.Latitude,
  DevicesModuleAttribute.Longitude,
  DevicesModuleAttribute.TorConnection,
  DevicesModuleAttribute.VpnConnection,
  DevicesModuleAttribute.ProxyConnection,
  DevicesModuleAttribute.DatacenterConnection,
  DevicesModuleAttribute.ServiceProvider,
  DevicesModuleAttribute.IpConnectionType,
];
const DEFAULT_LAYOUT_DEVICE = [
  DevicesModuleAttribute.DeviceHandoffMethod,
  DevicesModuleAttribute.Type,
  DevicesModuleAttribute.Os,
  DevicesModuleAttribute.DeviceFingerprint,
  DevicesModuleAttribute.Browser,
  DevicesModuleAttribute.BrowserFingerprint,
  DevicesModuleAttribute.BrowserFingerprint,
  DevicesModuleAttribute.GpsLatitude,
  DevicesModuleAttribute.GpsLongitude,
];

const InquiryDeviceSession = ({
  layoutNetwork = DEFAULT_LAYOUT_NETWORK,
  layoutDevice = DEFAULT_LAYOUT_DEVICE,
  inquirySession,
  device,
  isRedacted,
}: InquiryDeviceSessionProps) => {
  const hasInquiriesEnableDeviceFingerprint = useProductFeature(
    ProductFeatureName.InquiriesEnableDeviceFingerprint,
  );

  const {
    'threat-level': threatLevel,
    'ip-address': ipAddress,
    'country-name': countryName,
    'region-name': regionName,
    'gps-latitude': gpsLatitude,
    'gps-longitude': gpsLongitude,
    latitude,
    longitude,
    'device-handoff-method': deviceHandoffMethod,
    'is-proxy': isProxy,
    'is-tor': isTor,
    'is-datacenter': isDatacenter,
    'is-vpn': isVpn,
    'ip-isp': ipIsp,
    'ip-connection-type': ipConnectionType,
  } = inquirySession.attributes;

  const {
    'browser-full-version': browserFullVersion,
    'browser-name': browserName,
    'device-type': deviceType,
    'device-name': deviceName,
    'os-full-version': osFullVersion,
    'mobile-sdk-name': mobileSdkName,
    'os-name': osName,
    'browser-fingerprint': browserFingerprint,
    'device-fingerprint': deviceFingerprint,
    'device-vendor-id': deviceVendorId,
  } = device?.attributes || {};

  const deviceToken = inquirySession.relationships.device.data?.id;

  // Note: the boolean values can be null if unknown
  const threatLevelDisplay: ReactNode = threatLevel != null ? capitalize(threatLevel) : null;
  // NOTE: Borrowed from Pii -> front-end/dashboard/components/Pii/Pii.tsx
  const redactedSticker = <Sticker>Redacted</Sticker>;

  return (
    <ColumnWrapper>
      <Column>
        <Card.Title icon={<IconGlobe />}>Network details</Card.Title>
        <AttributeGrid horizontal='full-width'>
          {layoutNetwork.map((attribute) => {
            switch (attribute) {
              case DevicesModuleAttribute.IpAddress:
                return (
                  <Attribute
                    label='IP address'
                    key='IP address'
                    value={isRedacted ? redactedSticker : ipAddress}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.ThreatRating:
                return (
                  <Attribute
                    label='Network threat level'
                    key='Network threat level'
                    value={threatLevelDisplay}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.NetworkCountry:
                return (
                  <Attribute
                    label='Network country'
                    key='Network country'
                    value={isRedacted ? redactedSticker : countryName}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.NetworkRegion:
                return (
                  <Attribute
                    label='Network region'
                    key='Network region'
                    value={isRedacted ? redactedSticker : regionName}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.Latitude:
                return (
                  <Attribute
                    label='IP Latitude'
                    key='Latitude'
                    value={isRedacted ? redactedSticker : latitude}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.Longitude:
                return (
                  <Attribute
                    label='IP Longitude'
                    key='Longitude'
                    value={isRedacted ? redactedSticker : longitude}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.TorConnection:
                return (
                  <Attribute
                    label='Tor connection'
                    key='Tor connection'
                    value={<YesOrNo value={!!isTor} />}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.ProxyConnection:
                return (
                  <Attribute
                    label='Public proxy'
                    key='Public proxy'
                    value={<YesOrNo value={!!isProxy} />}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.DatacenterConnection:
                return (
                  <Attribute
                    label='Private proxy'
                    key='Private proxy'
                    value={<YesOrNo value={!!isDatacenter} />}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.VpnConnection:
                return (
                  <Attribute
                    label='VPN'
                    key='VPN'
                    value={<YesOrNo value={!!isVpn} />}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.ServiceProvider:
                return <Attribute label='ISP' key='ISP' value={ipIsp} placeholder='Unknown' />;
              case DevicesModuleAttribute.IpConnectionType:
                return (
                  <Attribute
                    label='IP Connection Type'
                    key='IpConnectionType'
                    value={ipConnectionType}
                    placeholder='Unknown'
                  />
                );
            }
          })}
        </AttributeGrid>
      </Column>
      <Spacer height={3} />
      <Column>
        <Card.Title icon={<DeviceIcon deviceType={deviceType ?? DeviceType.Desktop} />}>
          Device details
        </Card.Title>
        <AttributeGrid horizontal='full-width'>
          <Attribute
            label='Device token'
            key='Device token'
            value={deviceToken}
            placeholder='Unknown'
          />
          {layoutDevice.map((attribute) => {
            switch (attribute) {
              case DevicesModuleAttribute.Type:
                return (
                  <Attribute
                    label='Device type'
                    key='Device type'
                    value={formatDeviceMetadata([deviceName, deviceType], ', ')}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.DeviceHandoffMethod:
                return (
                  <Attribute
                    label='Device handoff method'
                    key='Device handoff method'
                    value={deviceHandoffMethod}
                    placeholder='N/A'
                  />
                );
              case DevicesModuleAttribute.Os:
                return (
                  <Attribute
                    label='Device OS'
                    key='Device OS'
                    value={formatDeviceMetadata([osName, osFullVersion], ' ')}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.DeviceFingerprint:
                if (!hasInquiriesEnableDeviceFingerprint) {
                  return null;
                }

                if (mobileSdkName) {
                  // If the device is using the mobile SDK, display the device vendor ID
                  return (
                    <Attribute
                      label={
                        <>
                          Device fingerprint (Mobile SDK){' '}
                          <HelpIcon popoverWidth={300}>
                            Unique identifier provided by the SDK, re-identifies the device even
                            when a user switches accounts
                          </HelpIcon>{' '}
                        </>
                      }
                      key='Device fingerprint (Mobile SDK)'
                      value={deviceVendorId}
                      placeholder='Unknown'
                    />
                  );
                } else {
                  return (
                    <Attribute
                      label={
                        <>
                          Device fingerprint (Browser){' '}
                          <HelpIcon popoverWidth={300}>
                            Unique value computed from a systematic collection of information about
                            a device
                          </HelpIcon>{' '}
                        </>
                      }
                      key='Device fingerprint (Browser)'
                      value={deviceFingerprint}
                      placeholder='Unknown'
                    />
                  );
                }
              case DevicesModuleAttribute.Browser:
                if (mobileSdkName) {
                  return null;
                } else {
                  return (
                    <Attribute
                      label='Browser'
                      key='Browser'
                      value={formatDeviceMetadata([browserName, browserFullVersion], ' ')}
                      placeholder='Unknown'
                    />
                  );
                }
              case DevicesModuleAttribute.BrowserFingerprint:
                if (mobileSdkName) {
                  return null;
                } else {
                  return (
                    <Attribute
                      label={
                        <>
                          Browser fingerprint{' '}
                          <HelpIcon popoverWidth={300}>
                            Unique value computed from a systematic collection of information about
                            the browser
                          </HelpIcon>{' '}
                        </>
                      }
                      key='Browser fingerprint'
                      value={browserFingerprint}
                      placeholder='Unknown'
                    />
                  );
                }
              case DevicesModuleAttribute.GpsLatitude:
                return (
                  <Attribute
                    label='GPS Latitude'
                    key='GpsLatitude'
                    value={isRedacted ? redactedSticker : gpsLatitude}
                    placeholder='Unknown'
                  />
                );
              case DevicesModuleAttribute.GpsLongitude:
                return (
                  <Attribute
                    label='GPS Longitude'
                    key='GpsLongitude'
                    value={isRedacted ? redactedSticker : gpsLongitude}
                    placeholder='Unknown'
                  />
                );
            }
          })}
        </AttributeGrid>
      </Column>
      <Spacer height={3} />
    </ColumnWrapper>
  );
};

const YesOrNo = ({ value }: { value: boolean }) =>
  value ? (
    <>
      Yes <IconAlertCircleStyled color={Color.CoreYellow800} />{' '}
    </>
  ) : (
    <>No</>
  );

/**
 * Some browsers inject double quotes in their device info headers. We've fixed this on the server,
 * but existing devices still have incorrect data.
 * This function prettifies such data.
 */
const formatDeviceMetadata = (
  attributes: Array<string | null | undefined> = [],
  joinChar: string,
): string | undefined =>
  attributes
    .map((str) => {
      if (str == null) return str;

      if (str.startsWith('"') && str.endsWith('"')) {
        return str.substring(1, str.length - 1);
      } else {
        return str;
      }
    })
    .filter((str): str is string => !!str)
    .join(joinChar);

export default InquiryDeviceSession;
